import { useContext, useCallback } from 'react';
import { AppStateContext } from '../contexts/AppStateContext';
import { TradingContext } from '../contexts/TradingContext';
import { StreamsContext } from '../contexts/StreamsContext';
import { BannerContext } from '../contexts/BannerContext';
import { AuthContext } from '../contexts/AuthContext';
import { isTrackTier } from '../utils/authAccess';
import { getDefaultTimeInForce } from '../utils/marketHours';
import { parseQuoteLast } from '../utils/quoteNetChangePct';
import { evenSplitTakeProfitPercents } from '../utils/bracketTpQuantities';
import { roundToTickSizeFromDetails } from '../services/tradestationApi';

/**
 * Applies an AI chat order draft (from the build_order_draft chat tool) to the
 * active chart: switches the ticker if needed and seeds order-type/quantity/
 * stop-loss/take-profit state so the existing bracket draft pipeline (PriceLines,
 * BracketLineOverlays) renders it. Never submits an order — draft only.
 *
 * order.sizingMode ('shares' | 'amount' | 'risk') mirrors the trade panel's own
 * three sizing modes — this sets positionSizeMode plus the corresponding field
 * (spendAmount or risk) so the panel's existing useQuantityCalculation hook
 * derives the final share count the same way it would from manual entry. Risk
 * quantity uses the symbol point value (dollars per point), same as this hook's
 * recalc, so a futures draft doesn't get resized when it lands on the chart.
 * The server quantity is seeded immediately, ahead of that recalc settling.
 *
 * Shared by AiOrderDraftListener (auto-apply when the draft is first generated)
 * and the "Add to chart" button on OrderDraftCard (re-apply an older draft from
 * chat history, e.g. after navigating away or reloading the page).
 *
 * Drawing the draft also requires the same state the manual "new bracket" UI
 * sets: targetingModeEnabled true and a reference entry price in limitPrice
 * (PriceLines/BracketLineOverlays gate the SL/TP lines on both, even for a
 * market order — see isActiveEntryBracketDraft in chartPriceLineVisibility.js).
 */
export default function useApplyOrderDraft() {
  const appState = useContext(AppStateContext);
  const {
    selectedTicker,
    activeChartId,
    updateChartSettings,
    // These fall back to AppStateContext's older unsuffixed setter names in
    // case a stale bundle predates the *Validated/*Server aliases being added.
    setQuantityValidated = appState?.setQuantity,
    setOrderTypeServer = appState?.setOrderType,
    setLimitPriceValidated = appState?.setLimitPrice,
    setStopLossValidated = appState?.setStopLoss,
    setTimeInForceServer = appState?.setTimeInForce,
    setRisk = appState?.setAccountSpecificRisk,
    setSpendAmount,
    setPositionSizeMode,
    setManualQuantityOverride,
    setEntryTakeProfits,
    setBracketDraftMode,
    setBracketType,
    setTargetingModeEnabled,
  } = appState || {};
  const { selectedTickerDetails } = useContext(TradingContext);
  const { quotesBySymbol } = useContext(StreamsContext);
  const { showMessage } = useContext(BannerContext);
  const { user } = useContext(AuthContext);

  return useCallback((draft) => {
    if (!draft || draft.status !== 'ok' || !draft.order) return;
    if (isTrackTier(user)) {
      showMessage?.('Order and bracket drafts are part of the Trade plan.', 'warning');
      return;
    }

    // Guards against rendering this card somewhere without the trading
    // providers (e.g. the public shared-chat page) — fail quietly instead of
    // throwing on a missing context setter.
    if (typeof setOrderTypeServer !== 'function' || typeof setTargetingModeEnabled !== 'function') {
      console.error('[useApplyOrderDraft] Trading context unavailable — cannot apply order draft here.');
      showMessage?.('Open this chat from the trading app to apply the order to a chart.', 'warning');
      return;
    }

    const { order } = draft;
    const symbol = order.symbol || selectedTicker;
    const orderType = order.orderType || 'market';
    const entryPrice = orderType === 'limit' ? order.limitPrice : parseQuoteLast(quotesBySymbol?.[symbol]);

    if (symbol && symbol !== selectedTicker) {
      updateChartSettings(activeChartId, { ticker: symbol });
    }
    setOrderTypeServer(orderType);
    // Seed quantity immediately as a best guess; for amount/risk sizing the
    // trade panel's own useQuantityCalculation recomputes it from the mode's
    // field below once stopLoss/entry are in place, same as manual entry.
    setQuantityValidated(order.quantity);
    setTimeInForceServer(getDefaultTimeInForce(orderType));
    setTargetingModeEnabled(true);
    if (Number.isFinite(entryPrice)) {
      setLimitPriceValidated(entryPrice);
    }

    // Match TradeTicket's mode switch: shares and amount lock the typed quantity,
    // risk leaves it derived. Otherwise a panel already set to "risk $100" rewrites
    // an explicit share count as soon as the bracket's stop is applied.
    if (order.sizingMode === 'amount' && Number.isFinite(order.sizingValue)) {
      setPositionSizeMode?.('amount');
      setSpendAmount?.(order.sizingValue);
      setManualQuantityOverride?.(true);
    } else if (order.sizingMode === 'risk' && Number.isFinite(order.sizingValue)) {
      setPositionSizeMode?.('risk');
      setRisk?.(order.sizingValue);
      setManualQuantityOverride?.(false);
    } else {
      setPositionSizeMode?.('shares');
      setManualQuantityOverride?.(true);
    }

    if (order.includeBracket && Number.isFinite(order.stopLoss) && order.takeProfits?.length) {
      const stopLoss = roundToTickSizeFromDetails(order.stopLoss, symbol, selectedTickerDetails);
      setStopLossValidated(stopLoss);

      const hasPercentages = order.takeProfits.every((tp) => Number.isFinite(tp.percentage));
      const percents = hasPercentages
        ? order.takeProfits.map((tp) => tp.percentage)
        : evenSplitTakeProfitPercents(order.takeProfits.length).map((cfg) => cfg.percentage);

      const legs = order.takeProfits.map((tp, i) => ({
        price: roundToTickSizeFromDetails(tp.price, symbol, selectedTickerDetails),
        percentage: percents[i],
        rMultiple: i + 1,
      }));

      setEntryTakeProfits(legs);
      setBracketType(legs.length >= 2 ? 'tp_2' : 'tp_1');
      setBracketDraftMode('manual');
    } else {
      setBracketType('none');
      setBracketDraftMode(null);
    }

    if (draft.message) showMessage(draft.message, 'success');
  }, [
    selectedTicker,
    activeChartId,
    updateChartSettings,
    setOrderTypeServer,
    setLimitPriceValidated,
    setQuantityValidated,
    setStopLossValidated,
    setEntryTakeProfits,
    setBracketType,
    setBracketDraftMode,
    setTimeInForceServer,
    setTargetingModeEnabled,
    setRisk,
    setSpendAmount,
    setPositionSizeMode,
    setManualQuantityOverride,
    selectedTickerDetails,
    quotesBySymbol,
    showMessage,
    user,
  ]);
}
